import {
  RECAPTURE_CAMPAIGNS,
  RECAPTURE_SCHEDULES,
  RECAPTURE_TEMPLATES,
  RECAPTURE_CALL_TEMPLATES,
  RECAPTURE_SMS_PROFILES,
  PATIENT_GROUP_RESOURCE_LIST,
  CLEAR_RECAPTURE_ERRORS,
} from '../actions/patientRecapture';

const initialState = {
  // Campaigns
  campaigns: [],
  campaignsLoading: false,
  campaignsError: null,
  campaignSaving: false,
  campaignError: null,

  // Schedules
  schedules: [],
  schedulesLoading: false,
  schedulesError: null,
  scheduleSaving: false,
  scheduleError: null,

  // Templates
  templates: [],
  templatesLoading: false,
  templatesError: null,
  templateSaving: false,
  templateError: null,

  // Call Templates
  callTemplates: [],
  callPromptProfiles: [],
  callTemplatesLoading: false,
  callTemplatesError: null,
  callTemplateSaving: false,
  callTemplateError: null,

  // Code Groups
  codeGroups: [],
  codeGroupsLoading: false,
  codeGroupsError: null,
  codeGroupSaving: false,
  codeGroupError: null,
  currentCodeGroup: null,
  currentCodeGroupLoading: false,

  // Appointment Type Groups
  appointmentTypeGroups: [],
  appointmentTypeGroupsLoading: false,
  appointmentTypeGroupsError: null,
  appointmentTypeGroupSaving: false,
  appointmentTypeGroupError: null,
  currentAppointmentTypeGroup: null,
  currentAppointmentTypeGroupLoading: false,

  // SMS Profiles
  smsProfiles: [],
  smsProfilesLoading: false,
  smsProfilesError: null,
};

const extractError = (action) => {
  return action?.payload?.response?.data?.error
    || action?.payload?.response?.data?.errors?.[0]
    || 'An unexpected error occurred';
};

const findPatientGroupResource = (actionType) => (
  PATIENT_GROUP_RESOURCE_LIST.find((resource) => Object.values(resource.actionTypes).includes(actionType))
);

const reducePatientGroupResource = (state, action) => {
  const resource = findPatientGroupResource(action.type);
  if (!resource) return null;

  const { actionTypes } = resource;
  const { state: resourceState } = resource;
  switch (action.type) {
    case actionTypes.FETCH_BEGIN:
      return {
        ...state,
        [resourceState.collectionLoading]: true,
        [resourceState.collectionError]: null,
      };
    case actionTypes.FETCH_SUCCESS:
      return {
        ...state,
        [resourceState.collectionLoading]: false,
        [resourceState.collection]: action.payload.data[resource.collectionPayloadKey] || [],
      };
    case actionTypes.FETCH_FAIL:
      return {
        ...state,
        [resourceState.collectionLoading]: false,
        [resourceState.collectionError]: extractError(action),
      };

    case actionTypes.FETCH_ONE_BEGIN:
      return {
        ...state,
        [resourceState.currentLoading]: true,
        [resourceState.current]: null,
      };
    case actionTypes.FETCH_ONE_SUCCESS:
      return {
        ...state,
        [resourceState.currentLoading]: false,
        [resourceState.current]: action.payload.data[resource.detailPayloadKey] || null,
      };
    case actionTypes.FETCH_ONE_FAIL:
      return {
        ...state,
        [resourceState.currentLoading]: false,
        [resourceState.error]: extractError(action),
      };

    case actionTypes.CREATE_BEGIN:
    case actionTypes.UPDATE_BEGIN:
    case actionTypes.ACTIVATE_BEGIN:
    case actionTypes.DEACTIVATE_BEGIN:
      return {
        ...state,
        [resourceState.saving]: true,
        [resourceState.error]: null,
      };
    case actionTypes.CREATE_SUCCESS:
    case actionTypes.UPDATE_SUCCESS:
    case actionTypes.ACTIVATE_SUCCESS:
    case actionTypes.DEACTIVATE_SUCCESS:
      return {
        ...state,
        [resourceState.saving]: false,
      };
    case actionTypes.CREATE_FAIL:
    case actionTypes.UPDATE_FAIL:
    case actionTypes.ACTIVATE_FAIL:
    case actionTypes.DEACTIVATE_FAIL:
      return {
        ...state,
        [resourceState.saving]: false,
        [resourceState.error]: extractError(action),
      };
    default:
      return null;
  }
};

export default (state, action) => {
  const currentState = state || initialState;
  const patientGroupState = reducePatientGroupResource(currentState, action);
  if (patientGroupState) return patientGroupState;

  switch (action.type) {
    // ============ CAMPAIGNS ============
    case RECAPTURE_CAMPAIGNS.FETCH_BEGIN:
      return {
        ...state,
        campaignsLoading: true,
        campaignsError: null,
      };
    case RECAPTURE_CAMPAIGNS.FETCH_SUCCESS:
      return {
        ...state,
        campaignsLoading: false,
        campaigns: action.payload.data.campaigns || [],
      };
    case RECAPTURE_CAMPAIGNS.FETCH_FAIL:
      return {
        ...state,
        campaignsLoading: false,
        campaignsError: extractError(action),
      };

    case RECAPTURE_CAMPAIGNS.CREATE_BEGIN:
    case RECAPTURE_CAMPAIGNS.UPDATE_BEGIN:
    case RECAPTURE_CAMPAIGNS.ACTIVATE_BEGIN:
    case RECAPTURE_CAMPAIGNS.DEACTIVATE_BEGIN:
    case RECAPTURE_CAMPAIGNS.REORDER_BEGIN:
      return {
        ...state,
        campaignSaving: true,
        campaignError: null,
      };
    case RECAPTURE_CAMPAIGNS.CREATE_SUCCESS:
    case RECAPTURE_CAMPAIGNS.UPDATE_SUCCESS:
    case RECAPTURE_CAMPAIGNS.ACTIVATE_SUCCESS:
    case RECAPTURE_CAMPAIGNS.DEACTIVATE_SUCCESS:
      return {
        ...state,
        campaignSaving: false,
      };
    case RECAPTURE_CAMPAIGNS.REORDER_SUCCESS:
      return {
        ...state,
        campaignSaving: false,
        campaigns: action.payload.data.campaigns || state.campaigns,
      };
    case RECAPTURE_CAMPAIGNS.CREATE_FAIL:
    case RECAPTURE_CAMPAIGNS.UPDATE_FAIL:
    case RECAPTURE_CAMPAIGNS.ACTIVATE_FAIL:
    case RECAPTURE_CAMPAIGNS.DEACTIVATE_FAIL:
    case RECAPTURE_CAMPAIGNS.REORDER_FAIL:
      return {
        ...state,
        campaignSaving: false,
        campaignError: extractError(action),
      };

    // ============ SCHEDULES ============
    case RECAPTURE_SCHEDULES.FETCH_BEGIN:
      return {
        ...state,
        schedulesLoading: true,
        schedulesError: null,
      };
    case RECAPTURE_SCHEDULES.FETCH_SUCCESS:
      return {
        ...state,
        schedulesLoading: false,
        schedules: action.payload.data.schedules || [],
      };
    case RECAPTURE_SCHEDULES.FETCH_FAIL:
      return {
        ...state,
        schedulesLoading: false,
        schedulesError: extractError(action),
      };

    case RECAPTURE_SCHEDULES.CREATE_BEGIN:
    case RECAPTURE_SCHEDULES.UPDATE_BEGIN:
    case RECAPTURE_SCHEDULES.ACTIVATE_BEGIN:
    case RECAPTURE_SCHEDULES.DEACTIVATE_BEGIN:
      return {
        ...state,
        scheduleSaving: true,
        scheduleError: null,
      };
    case RECAPTURE_SCHEDULES.CREATE_SUCCESS:
    case RECAPTURE_SCHEDULES.UPDATE_SUCCESS:
    case RECAPTURE_SCHEDULES.ACTIVATE_SUCCESS:
    case RECAPTURE_SCHEDULES.DEACTIVATE_SUCCESS:
      return {
        ...state,
        scheduleSaving: false,
      };
    case RECAPTURE_SCHEDULES.CREATE_FAIL:
    case RECAPTURE_SCHEDULES.UPDATE_FAIL:
    case RECAPTURE_SCHEDULES.ACTIVATE_FAIL:
    case RECAPTURE_SCHEDULES.DEACTIVATE_FAIL:
      return {
        ...state,
        scheduleSaving: false,
        scheduleError: extractError(action),
      };

    // ============ TEMPLATES ============
    case RECAPTURE_TEMPLATES.FETCH_BEGIN:
      return {
        ...state,
        templatesLoading: true,
        templatesError: null,
      };
    case RECAPTURE_TEMPLATES.FETCH_SUCCESS:
      return {
        ...state,
        templatesLoading: false,
        templates: action.payload.data.templates || [],
      };
    case RECAPTURE_TEMPLATES.FETCH_FAIL:
      return {
        ...state,
        templatesLoading: false,
        templatesError: extractError(action),
      };

    case RECAPTURE_TEMPLATES.CREATE_BEGIN:
    case RECAPTURE_TEMPLATES.UPDATE_BEGIN:
    case RECAPTURE_TEMPLATES.ACTIVATE_BEGIN:
    case RECAPTURE_TEMPLATES.DEACTIVATE_BEGIN:
      return {
        ...state,
        templateSaving: true,
        templateError: null,
      };
    case RECAPTURE_TEMPLATES.CREATE_SUCCESS:
    case RECAPTURE_TEMPLATES.UPDATE_SUCCESS:
    case RECAPTURE_TEMPLATES.ACTIVATE_SUCCESS:
    case RECAPTURE_TEMPLATES.DEACTIVATE_SUCCESS:
      return {
        ...state,
        templateSaving: false,
      };
    case RECAPTURE_TEMPLATES.CREATE_FAIL:
    case RECAPTURE_TEMPLATES.UPDATE_FAIL:
    case RECAPTURE_TEMPLATES.ACTIVATE_FAIL:
    case RECAPTURE_TEMPLATES.DEACTIVATE_FAIL:
      return {
        ...state,
        templateSaving: false,
        templateError: extractError(action),
      };

    // ============ CALL TEMPLATES ============
    case RECAPTURE_CALL_TEMPLATES.FETCH_BEGIN:
      return {
        ...state,
        callTemplatesLoading: true,
        callTemplatesError: null,
      };
    case RECAPTURE_CALL_TEMPLATES.FETCH_SUCCESS:
      return {
        ...state,
        callTemplatesLoading: false,
        callTemplates: action.payload.data.call_templates || [],
        callPromptProfiles: action.payload.data.prompt_profiles || [],
      };
    case RECAPTURE_CALL_TEMPLATES.FETCH_FAIL:
      return {
        ...state,
        callTemplatesLoading: false,
        callTemplatesError: extractError(action),
      };

    case RECAPTURE_CALL_TEMPLATES.CREATE_BEGIN:
    case RECAPTURE_CALL_TEMPLATES.UPDATE_BEGIN:
    case RECAPTURE_CALL_TEMPLATES.ACTIVATE_BEGIN:
    case RECAPTURE_CALL_TEMPLATES.DEACTIVATE_BEGIN:
      return {
        ...state,
        callTemplateSaving: true,
        callTemplateError: null,
      };
    case RECAPTURE_CALL_TEMPLATES.CREATE_SUCCESS:
    case RECAPTURE_CALL_TEMPLATES.UPDATE_SUCCESS:
    case RECAPTURE_CALL_TEMPLATES.ACTIVATE_SUCCESS:
    case RECAPTURE_CALL_TEMPLATES.DEACTIVATE_SUCCESS:
      return {
        ...state,
        callTemplateSaving: false,
      };
    case RECAPTURE_CALL_TEMPLATES.CREATE_FAIL:
    case RECAPTURE_CALL_TEMPLATES.UPDATE_FAIL:
    case RECAPTURE_CALL_TEMPLATES.ACTIVATE_FAIL:
    case RECAPTURE_CALL_TEMPLATES.DEACTIVATE_FAIL:
      return {
        ...state,
        callTemplateSaving: false,
        callTemplateError: extractError(action),
      };

    // ============ SMS PROFILES ============
    case RECAPTURE_SMS_PROFILES.FETCH_BEGIN:
      return {
        ...state,
        smsProfilesLoading: true,
        smsProfilesError: null,
      };
    case RECAPTURE_SMS_PROFILES.FETCH_SUCCESS:
      return {
        ...state,
        smsProfilesLoading: false,
        smsProfiles: action.payload.data.sms_profiles || [],
      };
    case RECAPTURE_SMS_PROFILES.FETCH_FAIL:
      return {
        ...state,
        smsProfilesLoading: false,
        smsProfilesError: extractError(action),
      };

    // ============ CLEAR ERRORS ============
    case CLEAR_RECAPTURE_ERRORS:
      return {
        ...state,
        campaignsError: null,
        campaignError: null,
        schedulesError: null,
        scheduleError: null,
        templatesError: null,
        templateError: null,
        callTemplatesError: null,
        callTemplateError: null,
        codeGroupsError: null,
        codeGroupError: null,
        appointmentTypeGroupsError: null,
        appointmentTypeGroupError: null,
        smsProfilesError: null,
      };

    default:
      return currentState;
  }
};
